/**
 * Runtime configuration.
 *
 * In production the container writes `window.RUNTIME_CONFIG` at startup (see
 * `env.sh`), so one image can be promoted through Test → Staging → Production
 * without rebuilding. In dev we fall back to Vite's compile-time `import.meta.env`.
 * This mirrors how the Web repo handles the same problem.
 */

export type RuntimeConfig = {
  loginPortal: string;
  clientId: string;
  redirectUri: string;
  apiBaseUrl: string;
  testLoginEnabled: boolean;
  testLoginSecret: string;
  oidcAudience: string;
  /** PostHog project key — empty disables analytics AND the budget-model flag (ADR 0027). */
  posthogKey: string;
  posthogHost: string;
};

declare global {
  interface Window {
    RUNTIME_CONFIG?: Partial<RuntimeConfig>;
  }
}

const isTruthy = (value: string | boolean | undefined): boolean =>
  value === true || value === 'true' || value === '1';

// Guarded so this module stays importable outside a browser (unit tests, tooling).
const injected = typeof window === 'undefined' ? undefined : window.RUNTIME_CONFIG;
const origin = typeof window === 'undefined' ? '' : window.location.origin;

export const config: RuntimeConfig = {
  loginPortal:
    injected?.loginPortal ?? import.meta.env.VITE_LOGIN_PORTAL ?? 'https://login.timelog.com',
  clientId: injected?.clientId ?? import.meta.env.VITE_OIDC_CLIENT_ID ?? 'phoenix-web',
  redirectUri:
    injected?.redirectUri ?? import.meta.env.VITE_OIDC_REDIRECT_URI ?? `${origin}/callback`,
  apiBaseUrl: injected?.apiBaseUrl ?? import.meta.env.VITE_API_BASE_URL ?? '/api',
  oidcAudience: injected?.oidcAudience ?? import.meta.env.VITE_OIDC_AUDIENCE ?? 'tlp',

  // Local/CI only. The backend independently refuses these tokens unless it too
  // has test-login enabled, so flipping this alone grants nothing.
  testLoginEnabled:
    injected?.testLoginEnabled !== undefined
      ? isTruthy(injected.testLoginEnabled)
      : isTruthy(import.meta.env.VITE_TEST_LOGIN_ENABLED),
  testLoginSecret: injected?.testLoginSecret ?? import.meta.env.VITE_TEST_LOGIN_SECRET ?? '',

  // No key ⇒ posthog-js is never initialised and the budget model stays at the
  // control defaults. Playwright pins the key empty for exactly that reason.
  posthogKey: injected?.posthogKey ?? import.meta.env.VITE_POSTHOG_KEY ?? '',
  posthogHost:
    injected?.posthogHost ?? import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.posthog.com',
};

/**
 * The Agentation visual-feedback toolbar — a dev-only overlay that turns clicks on the
 * running UI into annotations an agent reads over MCP (see README > Visual feedback).
 *
 * On by default under `pnpm dev`. `import.meta.env.DEV` is a compile-time constant, so a
 * production build folds this to `false` and tree-shakes the import away. Playwright pins
 * `VITE_AGENTATION=off`: the toolbar's fixed overlay would sit in front of the timeline's
 * `elementFromPoint` drags (ADR 0019) and show up in the screenshot run.
 */
export const AGENTATION_ENABLED = import.meta.env.DEV && import.meta.env.VITE_AGENTATION !== 'off';

/**
 * Where the toolbar syncs annotations to: the `agentation-mcp server` HTTP endpoint,
 * port 4747 by default. Without a reachable server the toolbar still works, keeping
 * annotations in localStorage for copy/paste instead.
 */
export const AGENTATION_ENDPOINT =
  import.meta.env.VITE_AGENTATION_ENDPOINT ?? 'http://localhost:4747';

/**
 * The DEFAULT for the "Create sample (Project Ship)" affordance on the portfolio page.
 *
 * OFF by default, and deliberately so: the portfolio is the first screen an interview
 * participant sees, and a button that conjures a fabricated project sits badly next to a
 * list of their own. The flow itself is untouched (`useCreateSampleProject` builds the
 * whole fixture through the public API) — only its button is gated.
 *
 * Only the default, because Vite inlines `import.meta.env` when the server boots: an env
 * var cannot be flipped from inside the running app. The sidebar's dev control writes a
 * localStorage override on top of this, and `lib/sampleProject.ts` is what components read.
 *
 * Playwright pins `VITE_SAMPLE_PROJECT=on`: the fixture is what every planning, financials
 * and screenshot spec builds on. Like `VITE_AGENTATION`, a second web `webServer` entry
 * would need it too.
 */
export const SAMPLE_PROJECT_ENABLED = import.meta.env.VITE_SAMPLE_PROJECT === 'on';

/**
 * Whether to render developer-only controls — currently just the sidebar's
 * sample-data toggle, which flips `SAMPLE_PROJECT_ENABLED` at runtime
 * (`lib/sampleProject.ts`) so the env var above does not have to be remembered
 * and the dev server does not have to be restarted.
 *
 * `import.meta.env.DEV` is a compile-time constant, so a production build folds
 * this to `false` and tree-shakes the control away: a deployed environment —
 * staging included — never shows it, whatever is in localStorage.
 */
export const DEV_TOOLS_ENABLED = import.meta.env.DEV;

/**
 * The UI language for someone who has not used the sidebar's picker yet (ADR 0049).
 * Danish: the pilot customers plan in Danish. The picker's choice is stored per
 * browser (`i18n/language.ts`) and wins over this.
 *
 * Playwright pins `VITE_DEFAULT_LANGUAGE=en`: the e2e suite asserts English copy.
 */
export const DEFAULT_LANGUAGE: 'da' | 'en' =
  import.meta.env.VITE_DEFAULT_LANGUAGE === 'en' ? 'en' : 'da';

/** Scopes required to call the TimeLog APIs — same set the Web SPA requests. */
export const OIDC_SCOPE = 'openid profile tlp offline_access';
